/**
 * Canvas v1 runtime data validation.
 *
 * Canvas deliberately accepts only the nested element model. Legacy flat
 * elements must be converted by an offline migration before reaching runtime.
 */

import { z } from 'zod';
import { normalizePagesWorkspaceLayout } from '@/components/canvas/workspace/model/pageArrangement';
import type { Page } from '@/components/canvas/document/publicTypes';
import { ElementSchema } from '@/components/canvas/document/schema/elements';
import { PageSchema, GridGuideSettingsSchema, PageGuideSettingsSchema } from '@/components/canvas/page/schema';

export { ElementSchema } from '@/components/canvas/document/schema/elements';
export { GridGuideSettingsSchema, PageGuideSettingsSchema, PageSchema } from '@/components/canvas/page/schema';

/** @deprecated 使用 `PageSchema` */
export const CanvasSchema = PageSchema;

/** @deprecated 仅用于读取旧 JSON，解析后丢弃，不写入新文件 */
const LegacyCurrentPageIdSchema = z.string().optional();

export const CanvasDataSchema = z.object({
  schemaVersion: z.preprocess(
    (value) => (typeof value === 'number' || value == null ? 1 : value),
    z.literal(1),
  ),
  pages: z.array(PageSchema),
  currentPageId: LegacyCurrentPageIdSchema,
  workspaceElements: z.array(ElementSchema).default([]),
  viewport: z
    .object({
      zoom: z.number(),
      panX: z.number(),
      panY: z.number(),
    })
    .optional(),
  workspacePageLayoutMode: z.enum(['single', 'horizontal', 'vertical']).optional(),
});

/** @deprecated 使用 `CanvasDataSchema` */
export const CanvasFileDataSchema = CanvasDataSchema;

export type ParsedCanvasData = {
  schemaVersion: 1;
  pages: Page[];
  workspaceElements: ReturnType<typeof ElementSchema.parse>[];
  viewport?: { zoom: number; panX: number; panY: number };
  workspacePageLayoutMode?: 'single' | 'horizontal' | 'vertical';
};

export function parseCanvasData(raw: unknown): ParsedCanvasData | null {
  let payload: unknown = raw;
  if (raw != null && typeof raw === 'object' && 'data' in raw && (raw as { data?: unknown }).data != null) {
    const inner = (raw as { data: unknown }).data;
    if (typeof inner === 'object' && inner !== null && ('pages' in inner || 'canvases' in inner)) {
      payload = inner;
    }
  }

  const result = CanvasDataSchema.safeParse(payload);
  if (!result.success) {
    console.warn('[canvas] 文件数据校验失败，仅接受当前嵌套元素结构:', result.error.issues);
    return null;
  }

  const data = result.data;

  const pages = normalizePagesWorkspaceLayout(
    (data.pages as Page[]).map((page) => {
      const showGuides = page.viewState?.showGuides;
      const guideSettings =
        showGuides !== undefined && page.guideSettings.enabled !== showGuides
          ? { ...page.guideSettings, enabled: showGuides }
          : page.guideSettings;
      const { viewState: _legacyViewState, ...rest } = page;
      return { ...rest, guideSettings };
    }),
  );

  return {
    schemaVersion: 1,
    pages,
    workspaceElements: data.workspaceElements,
    viewport: data.viewport,
    workspacePageLayoutMode: data.workspacePageLayoutMode,
  };
}
